import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { formatDateTime } from '@/lib/format'
import { useI18n } from '@/lib/i18n'

interface CountdownProps {
    target: string
    mode?: 'ends' | 'starts'
    tone?: 'dark' | 'light'
    className?: string
    compact?: boolean
}

const pad = (value: number): string => String(value).padStart(2, '0')

/** Live countdown to an offer's end or start. Shows the plain date until the browser takes over. */
export const Countdown: FC<CountdownProps> = ({ target, mode = 'ends', tone = 'dark', className, compact }): ReactNode => {
    const { tx, locale } = useI18n()
    const [now, setNow] = useState<number | null>(null)
    const end = Date.parse(target)

    useEffect(() => {
        setNow(Date.now())

        const timer = window.setInterval(() => setNow(Date.now()), 1000)

        return () => window.clearInterval(timer)
    }, [])

    if (Number.isNaN(end)) return null

    const label = mode === 'ends' ? tx('ينتهي خلال', 'Ends in') : tx('يبدأ خلال', 'Starts in')
    const muted = tone === 'light' ? 'text-white/60' : 'text-muted-foreground'

    if (now === null) {
        return (
            <p className={cn('text-xs', muted, className)}>
                {mode === 'ends' ? tx('ينتهي', 'Ends') : tx('يبدأ', 'Starts')}
                {' '}
                <span suppressHydrationWarning>{formatDateTime(target, locale)}</span>
            </p>
        )
    }

    const remaining = Math.max(0, end - now)

    if (remaining === 0) {
        return <p className={cn('text-xs font-medium', muted, className)}>{mode === 'ends' ? tx('انتهى العرض', 'This offer has ended') : tx('بدأ العرض', 'This offer has started')}</p>
    }

    const days = Math.floor(remaining / 86400000)
    const hours = Math.floor((remaining % 86400000) / 3600000)
    const minutes = Math.floor((remaining % 3600000) / 60000)
    const seconds = Math.floor((remaining % 60000) / 1000)

    const units = [
        { value: days, label: tx('يوم', 'd') },
        { value: hours, label: tx('ساعة', 'h') },
        { value: minutes, label: tx('دقيقة', 'm') },
        { value: seconds, label: tx('ثانية', 's') }
    ]

    if (compact) {
        return (
            <p className={cn('inline-flex items-center gap-1.5 text-xs font-medium tabular-nums', tone === 'light' ? 'text-white/80' : 'text-navy-700', className)}>
                <span>{label}</span>
                <span dir='ltr'>{days > 0 ? `${days}d ` : ''}{pad(hours)}:{pad(minutes)}:{pad(seconds)}</span>
            </p>
        )
    }

    return (
        <div className={cn('flex flex-col gap-2', className)}>
            <span className={cn('text-xs font-medium', muted)}>{label}</span>
            <div className='flex items-center gap-1.5' dir='ltr'>
                {units.map((unit, index) => (
                    <div key={index} className={cn('flex min-w-13 flex-col items-center rounded-2xl px-2 py-2', tone === 'light' ? 'bg-white/10 ring-1 ring-white/10' : 'bg-navy-50 ring-1 ring-navy-100')}>
                        <span className={cn('text-lg font-bold leading-none tabular-nums', tone === 'light' ? 'text-white' : 'text-navy-950')}>{pad(unit.value)}</span>
                        <span className={cn('mt-1 text-[10px]', muted)}>{unit.label}</span>
                    </div>
                ))}
            </div>
        </div>
    )
}
